import React from "react";
import { Loader2 } from "lucide-react";

export const NeonCard = ({ variant = "flow", className = "", innerClassName = "p-5", children, ...rest }) => {
  if (variant === "flow") {
    return (
      <div className={`neon-flow ${className}`} {...rest}>
        <div className={`neon-inner ${innerClassName}`}>{children}</div>
      </div>
    );
  }
  return (
    <div className={`neon-${variant} rounded-[var(--radius)] bg-[var(--surface)] ${className}`} {...rest}>
      <div className={innerClassName}>{children}</div>
    </div>
  );
};

const BTN_BASE =
  "inline-flex items-center justify-center gap-2 font-display uppercase tracking-[0.12em] rounded-[12px] px-5 py-3 text-base leading-none transition-[transform,filter,background-color,box-shadow,color] duration-200 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black select-none";

const BTN_VARIANTS = {
  pink: "bg-[var(--neon-pink)] text-black shadow-[0_0_0_1px_rgba(255,45,149,.4),0_0_28px_rgba(255,45,149,.4)] hover:brightness-110 focus-visible:ring-[var(--neon-pink)]",
  green: "bg-[var(--neon-green)] text-black shadow-[0_0_0_1px_rgba(0,255,163,.4),0_0_28px_rgba(0,255,163,.4)] hover:brightness-110 focus-visible:ring-[var(--neon-green)]",
  cyan: "bg-[var(--neon-cyan)] text-black shadow-[0_0_0_1px_rgba(0,229,255,.4),0_0_28px_rgba(0,229,255,.4)] hover:brightness-110 focus-visible:ring-[var(--neon-cyan)]",
  outlinePink: "bg-transparent border border-[rgba(255,45,149,.7)] text-[var(--neon-pink)] shadow-[0_0_0_1px_rgba(255,45,149,.2),0_0_18px_rgba(255,45,149,.18)] hover:bg-[rgba(255,45,149,.10)] focus-visible:ring-[var(--neon-pink)]",
  outlineCyan: "bg-transparent border border-[rgba(0,229,255,.7)] text-[var(--neon-cyan)] shadow-[0_0_0_1px_rgba(0,229,255,.2),0_0_18px_rgba(0,229,255,.18)] hover:bg-[rgba(0,229,255,.10)] focus-visible:ring-[var(--neon-cyan)]",
  outlineGreen: "bg-transparent border border-[rgba(0,255,163,.7)] text-[var(--neon-green)] shadow-[0_0_0_1px_rgba(0,255,163,.2),0_0_18px_rgba(0,255,163,.18)] hover:bg-[rgba(0,255,163,.10)] focus-visible:ring-[var(--neon-green)]",
  ghost: "bg-transparent text-[var(--text-muted)] hover:text-white",
};

export const NeonButton = ({ variant = "pink", className = "", children, as: Tag = "button", loading = false, disabled, ...rest }) => (
  <Tag className={`${BTN_BASE} ${BTN_VARIANTS[variant] || BTN_VARIANTS.pink} ${className}`} disabled={disabled || loading} {...rest}>
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
    {children}
  </Tag>
);

export const NeonPrice = ({ value, color = "green", className = "", testid }) => (
  <span data-testid={testid} className={`font-display leading-none glow-${color} ${className}`}>{value}</span>
);

export const SectionTitle = ({ children, accent = "white", className = "", sub }) => (
  <div className={className}>
    <h2 className={`font-display uppercase tracking-[0.08em] text-3xl sm:text-4xl ${accent === "white" ? "glow-white text-white" : `glow-${accent}`}`}>{children}</h2>
    {sub && <p className="mt-2 text-sm text-[var(--text-muted)] max-w-2xl">{sub}</p>}
  </div>
);

export const Spinner = ({ className = "" }) => (
  <div className={`flex items-center justify-center py-16 ${className}`}>
    <Loader2 className="h-8 w-8 animate-spin text-[var(--neon-cyan)]" />
  </div>
);

export const EmptyState = ({ title, subtitle, action }) => (
  <NeonCard variant="soft" className="text-center" innerClassName="p-10">
    <p className="font-display uppercase text-2xl tracking-wide text-white">{title}</p>
    {subtitle && <p className="mt-2 text-sm text-[var(--text-muted)]">{subtitle}</p>}
    {action && <div className="mt-5 flex justify-center">{action}</div>}
  </NeonCard>
);

export const Pill = ({ children, color = "pink", className = "" }) => {
  const map = {
    pink: "border-[rgba(255,45,149,.5)] text-[var(--neon-pink)]",
    cyan: "border-[rgba(0,229,255,.5)] text-[var(--neon-cyan)]",
    green: "border-[rgba(0,255,163,.5)] text-[var(--neon-green)]",
    gold: "border-[rgba(255,184,0,.5)] text-[var(--neon-gold)]",
    grey: "border-white/15 text-[var(--text-muted)]",
  };
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${map[color]} ${className}`}>{children}</span>;
};
